/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Dispatch, SetStateAction } from 'react';
import { Modal, BaseModalProps } from '@blend/shared-react';
import { colors, mixins, Markup } from '@blend/consumer-components';
import { css, StyleSheet } from 'aphrodite';
import { InjectedFormProps, reduxForm, SubmissionError } from 'redux-form';

import { TaskForm } from '../../form/TaskForm';
import { RequiredCheckboxInputConfig } from '../../form/TaskInputs/types';
import { TaskInput } from '../../form/TaskInputs/TaskInput/TaskInput';

import { NotaryAPI } from '../../../api/notary/types';
import { notaryAPI } from '../../../api/notary';
import { Wrapper, Error } from '@components/form/Inputs/InputError';
import { DefaultSubmitInput } from '@components/form/DefaultSubmitInput/DefaultSubmitInput';
import { formatNames } from '@utils/formatting';
const FORM_NAME = 'notary_consent_modal';

const overlayStyleConfig = {
  backgroundColor: colors.catalyst,
};

const styles = StyleSheet.create({
  contentWrapper: {
    ...mixins.margin(40, 40, 40, 40),
  },
  headerText: {
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 40,
  },
  helperText: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 24,
  },
  buttonWrapper: {
    marginTop: 30,
  },
  submitDescription: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    color: colors.catalystLight80,
    ...mixins.margin(30, 0, 0, 0),
  },
  consentsWrapper: {
    ...mixins.margin(16, 0, 0, 0),
  },
});

export namespace ConsentModal {
  export interface OwnProps extends Omit<BaseModalProps, 'handleClose'> {
    callId: string;
    onSubmitSuccess: () => void;
    participants: NotaryAPI.Participant[];
    isSubmitting: boolean;
    setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  }
  export type Props = InjectedFormProps<{}, OwnProps> & OwnProps;
}

// using TaskInput config so that we can reuse the TaskInput component for this form
const checkboxNotarialCommissionConfig: RequiredCheckboxInputConfig = {
  id: 'notarialCommisionConsentCheckbox',
  type: 'boolean_required_true',
  displayParts: [
    `I agree that my notarial commission is valid and active in the state in which I hold my commission, and that I am physically located in the state of my notarial commission. I agree to comply with all laws and regulations pertaining to the notarial act I am about to perform.`,
  ],
  required: true,
};

const consentOtherConsentsConfig: RequiredCheckboxInputConfig = {
  id: 'otherConsentsCheckbox',
  type: 'boolean_required_true',
  displayParts: [
    'I confirm that I have read and agree to the ',
    { textScreenId: 'TERMS_OF_USE' },
    ', ',
    { textScreenId: 'PRIVACY_POLICY' },
    ' and ',
    { textScreenId: 'CONSENT_TO_USE_ELECTRONIC_SIGNATURES_AND_RECORDS' },
    '.',
  ],
  required: true,
};

const _ConsentModal: React.FC<ConsentModal.Props> = ({ handleSubmit, error, ...props }) => {
  const { callId, onSubmitSuccess, participants, isSubmitting, setIsSubmitting, ...otherProps } = props;

  const formTemplate = (
    <>
      <TaskInput config={checkboxNotarialCommissionConfig} />
      <div className={css(styles.consentsWrapper)}>
        <TaskInput config={consentOtherConsentsConfig} />
      </div>
      <div className={css(styles.buttonWrapper)}>
        <DefaultSubmitInput disabled={isSubmitting}>Continue</DefaultSubmitInput>
      </div>
    </>
  );

  // format: "Bailey Borrower and Diego Borrower", or "Bailey Borrower, Diego Borrower, and Giuseppe Pappardelle"
  const primaryParticipants = participants.filter(participant => !participant.primaryParticipantId);
  const participantNames = primaryParticipants.map(
    participant => `<strong>${participant.firstName} ${participant.lastName}</strong>`,
  );
  const formattedParticipantNames = formatNames(participantNames);

  return (
    <Modal overlayStyleConfig={overlayStyleConfig} data-it="ConsentModal" handleClose={() => {}} {...otherProps}>
      <div className={css(styles.contentWrapper)}>
        <div className={css(styles.headerText)}>Attend your RON appointment</div>
        <div className={css(styles.helperText)}>
          It’s time for a signing appointment with <Markup content={formattedParticipantNames} />. Please be aware that
          this video call will be recorded for record-keeping and quality assurance.
        </div>
        <TaskForm onSubmit={handleSubmit} formTemplate={formTemplate} />
        {error && (
          <Wrapper id={'general-error'}>
            <Error error={error} />
          </Wrapper>
        )}
      </div>
    </Modal>
  );
};

export const ConsentModal = reduxForm<{}, ConsentModal.OwnProps>({
  form: FORM_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  onSubmit: async (
    _values: any,
    _dispatch: any,
    {
      callId,
      onSubmitSuccess,
      setIsSubmitting,
    }: { callId: string; onSubmitSuccess: () => void; setIsSubmitting: Dispatch<SetStateAction<boolean>> },
  ) => {
    setIsSubmitting(true);
    // generic way to handle any server errors
    const generalErrorMessage = `
      An error occurred during submission. Please refresh the page and try
      again, or contact us if you still have trouble.
    `;
    try {
      const response = await notaryAPI.calls.recordConsent(callId);
      if (response.status === 200) {
        onSubmitSuccess();
      } else {
        setIsSubmitting(false);
        throw new SubmissionError({ _error: generalErrorMessage });
      }
    } catch (e) {
      setIsSubmitting(false);
      throw new SubmissionError({ _error: generalErrorMessage });
    }
  },
})(_ConsentModal);
